import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

class MmBundleBuilder extends HTMLElement {
  /** @type {AbortController} */
  #listeners = new AbortController();

  /** @type {boolean} */
  #submitting = false;

  /** @type {import('swiper').Swiper | null} */
  #thumbsSwiper = null;

  connectedCallback() {
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    this.#initGallery();
    this.#bindRows(signal);
    this.#bindAtc(signal);
    this.#syncInitialRows();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate, { signal });
  }

  disconnectedCallback() {
    this.#listeners.abort();
  }

  get #minQty() {
    return Number(this.dataset.minQty) || 3;
  }

  get #requiredText() {
    return this.dataset.requiredText || 'Quantity Required';
  }

  get #defaultText() {
    return this.dataset.defaultText || 'Add to Cart';
  }

  get #showAtcPrice() {
    return this.dataset.showAtcPrice !== 'false';
  }

  get #showAtcComparePrice() {
    return this.dataset.showAtcComparePrice !== 'false';
  }

  get #rows() {
    return Array.from(this.querySelectorAll('.mm-bundle-builder__row'));
  }

  /** @typedef {{ el: Element, min: number, type: 'percent' | 'fixed', discount: number }} Tier */

  /** @returns {Tier[]} */
  get #tierEls() {
    return Array.from(this.querySelectorAll('.mm-bundle-builder__tier-col')).map((col) => ({
      el: col,
      min: Number(col.dataset.tierMin) || 0,
      type: col.dataset.tierDiscountType === 'fixed' ? 'fixed' : 'percent',
      discount: parseFloat(col.dataset.tierDiscount) || 0,
    }));
  }

  /** @returns {Tier[]} */
  get #tiers() {
    return this.#tierEls
      .filter((tier) => tier.discount > 0)
      .sort((a, b) => b.min - a.min);
  }

  /** @param {number} cents */
  #money(cents) {
    return formatMoney(
      Math.round(cents),
      this.dataset.moneyFormat || '${{amount}}',
      this.dataset.currency || 'USD'
    );
  }

  /**
   * Highest tier unlocked at this quantity, or null.
   * @param {number} totalQty
   * @returns {Tier | null}
   */
  #activeTier(totalQty) {
    for (const tier of this.#tiers) {
      if (totalQty >= tier.min) return tier;
    }
    return null;
  }

  /**
   * Discount applied to ONE unit, in integer cents, mirroring Shopify's own
   * allocation: for percentage discounts Shopify computes the discount per
   * unit and rounds it DOWN to the smallest currency unit, then multiplies by
   * quantity (e.g. $6.99 at 5% = 34.95¢ -> 34¢ -> $6.65/unit, 3 x $6.65 = $19.95).
   * Fixed "amount off each" discounts are already a per-unit cent value.
   * @param {number} unitPriceCents
   * @param {Tier | null} tier
   */
  #unitDiscountCents(unitPriceCents, tier) {
    if (!tier || tier.discount <= 0 || unitPriceCents <= 0) return 0;

    let cents;
    if (tier.type === 'fixed') {
      cents = Math.round(tier.discount * 100);
    } else {
      // Small epsilon guards against float noise like 34.949999 for an exact 34.95.
      cents = Math.floor((unitPriceCents * tier.discount) / 100 + 1e-9);
    }

    return Math.min(Math.max(cents, 0), unitPriceCents);
  }

  /**
   * Per-unit price after the tier discount, in integer cents.
   * @param {Element} row
   * @param {Tier | null} tier
   */
  #rowUnitPriceCents(row, tier) {
    const unitPriceCents = Math.round(Number(row.dataset.variantPrice)) || 0;
    return unitPriceCents - this.#unitDiscountCents(unitPriceCents, tier);
  }

  #initGallery() {
    if (typeof Swiper === 'undefined') return;

    const thumbsEl = this.querySelector('.mm-bundle-builder__thumbs-swiper');
    const heroEl = this.querySelector('.mm-bundle-builder__hero-swiper');

    if (thumbsEl) {
      this.#thumbsSwiper = new Swiper(thumbsEl, {
        slidesPerView: 3,
        spaceBetween: 16,
        watchSlidesProgress: true,
      });
    }

    if (heroEl) {
      new Swiper(heroEl, {
        slidesPerView: 1,
        spaceBetween: 0,
        grabCursor: true,
        thumbs: this.#thumbsSwiper ? { swiper: this.#thumbsSwiper } : undefined,
      });
    }

    if (thumbsEl) {
      thumbsEl.querySelectorAll('.swiper-slide').forEach((slide) => {
        slide.addEventListener('click', () => this.#bringThumbIntoView(slide), { signal: this.#listeners.signal });
      });
    }
  }

  /** @param {Element} slide */
  #bringThumbIntoView(slide) {
    const thumbsSwiper = this.#thumbsSwiper;
    if (!thumbsSwiper || !slide.parentNode) return;

    const siblings = slide.parentNode.children;
    const idx = Array.prototype.indexOf.call(siblings, slide);
    if (idx < 0) return;

    const viewport = thumbsSwiper.el.getBoundingClientRect();
    const rect = slide.getBoundingClientRect();
    const notFullyVisible = rect.left < viewport.left - 1 || rect.right > viewport.right + 1;
    const nextSlide = siblings[idx + 1];
    const prevSlide = siblings[idx - 1];
    const nextHidden = !!(nextSlide && nextSlide.getBoundingClientRect().right > viewport.right + 1);
    const prevHidden = !!(prevSlide && prevSlide.getBoundingClientRect().left < viewport.left - 1);

    if (notFullyVisible || nextHidden) {
      thumbsSwiper.slideTo(idx);
    } else if (prevHidden) {
      const perView = Number(thumbsSwiper.params.slidesPerView) || 1;
      thumbsSwiper.slideTo(Math.max(0, idx - (perView - 1)));
    }
  }

  /** @param {AbortSignal} signal */
  #bindRows(signal) {
    for (const row of this.#rows) {
      const addBtn = row.querySelector('[data-bb-add]');
      const minusBtn = row.querySelector('[data-bb-minus]');
      const plusBtn = row.querySelector('[data-bb-plus]');
      const input = row.querySelector('.mm-bundle-builder__qty-input');
      const available = row.dataset.variantAvailable === 'true';

      addBtn?.addEventListener(
        'click',
        () => {
          if (available) this.#setRowQty(row, 1);
        },
        { signal }
      );

      plusBtn?.addEventListener(
        'click',
        () => {
          const value = Number(input?.value) || 0;
          this.#setRowQty(row, value + 1);
        },
        { signal }
      );

      minusBtn?.addEventListener(
        'click',
        () => {
          const value = Number(input?.value) || 0;
          this.#setRowQty(row, value - 1);
        },
        { signal }
      );

      input?.addEventListener(
        'change',
        () => {
          const value = Number(input.value) || 0;
          this.#setRowQty(row, value);
        },
        { signal }
      );
    }
  }

  /** @param {AbortSignal} signal */
  #bindAtc(signal) {
    this.querySelector('[data-bb-atc]')?.addEventListener('click', (event) => {
      event.preventDefault();
      this.#addBundleToCart();
    }, { signal });
  }

  #syncInitialRows() {
    for (const row of this.#rows) {
      const qty = Number(row.querySelector('.mm-bundle-builder__qty-input')?.value) || 0;
      if (qty > 0) this.#setRowQty(row, qty);
    }
    this.#refreshAtc();
  }

  /** @param {Element} row */
  #setRowQty(row, newQty) {
    const qty = Math.max(0, newQty);
    const input = row.querySelector('.mm-bundle-builder__qty-input');
    const addBtn = row.querySelector('[data-bb-add]');
    const qtyWrap = row.querySelector('.mm-bundle-builder__qty');

    if (input instanceof HTMLInputElement) input.value = String(qty);

    if (qty > 0) {
      addBtn?.classList.add('is-hidden');
      qtyWrap?.classList.remove('is-hidden');
      row.classList.add('is-active');
    } else {
      addBtn?.classList.remove('is-hidden');
      qtyWrap?.classList.add('is-hidden');
      row.classList.remove('is-active');
    }

    this.#refreshAtc();
  }

  /**
   * All amounts in integer cents. The total is the sum of each row's
   * (rounded discounted unit price x quantity), never (unit discount x total
   * quantity), so it reproduces the cart drawer to the cent.
   */
  #getTotals() {
    const rows = this.#rows.map((row) => ({
      row,
      qty: Number(row.querySelector('.mm-bundle-builder__qty-input')?.value) || 0,
    }));

    const totalQty = rows.reduce((sum, item) => sum + item.qty, 0);
    const tier = this.#activeTier(totalQty);

    let originalCents = 0;
    let priceCents = 0;

    for (const { row, qty } of rows) {
      if (qty <= 0) continue;
      const unitPriceCents = Math.round(Number(row.dataset.variantPrice)) || 0;
      originalCents += unitPriceCents * qty;
      priceCents += this.#rowUnitPriceCents(row, tier) * qty;
    }

    return { qty: totalQty, price: priceCents, original: originalCents, tier };
  }

  /**
   * Update each row's per-bottle price to reflect the active tier discount,
   * showing the original price struck through when a discount applies.
   * @param {Tier | null} tier
   */
  #refreshRowPrices(tier) {
    for (const row of this.#rows) {
      const priceEl = row.querySelector('[data-bb-row-price]');
      const wasEl = row.querySelector('[data-bb-row-was]');
      if (!priceEl) continue;

      const unitPriceCents = Math.round(Number(row.dataset.variantPrice)) || 0;
      const saleCents = this.#rowUnitPriceCents(row, tier);
      const hasDiscount = saleCents < unitPriceCents;

      priceEl.textContent = this.#money(saleCents);
      row.classList.toggle('has-discount', hasDiscount);

      if (wasEl instanceof HTMLElement) {
        wasEl.textContent = hasDiscount ? this.#money(unitPriceCents) : '';
        wasEl.classList.toggle('is-hidden', !hasDiscount);
      }
    }
  }

  /** @param {{ qty: number, price: number, original: number, tier: Tier | null } | null} totals */
  #renderAtcPrices(totals) {
    const pricesEl = this.querySelector('[data-bb-atc-prices]');
    const priceEl = this.querySelector('[data-bb-atc-price]');
    const wasEl = this.querySelector('[data-bb-atc-was]');
    const wasValueEl = this.querySelector('[data-bb-atc-was-value]');

    if (!(pricesEl instanceof HTMLElement)) return;

    if (!totals) {
      pricesEl.classList.add('is-hidden');
      return;
    }

    const showPrice = this.#showAtcPrice;
    const hasDiscount = totals.original - totals.price >= 1;
    const showCompare = this.#showAtcComparePrice && hasDiscount;

    if (!showPrice && !(showCompare && hasDiscount)) {
      pricesEl.classList.add('is-hidden');
      return;
    }

    pricesEl.classList.remove('is-hidden');

    if (priceEl) {
      if (showPrice) {
        priceEl.textContent = this.#money(totals.price);
        priceEl.classList.remove('is-hidden');
      } else {
        priceEl.textContent = '';
        priceEl.classList.add('is-hidden');
      }
    }

    if (wasEl instanceof HTMLElement && wasValueEl) {
      if (showCompare && hasDiscount) {
        wasValueEl.textContent = this.#money(totals.original);
        wasEl.classList.remove('is-hidden');
      } else {
        wasValueEl.textContent = '';
        wasEl.classList.add('is-hidden');
      }
    }
  }

  /** @param {number} totalQty */
  #refreshTiers(totalQty) {
    for (const tier of this.#tierEls) {
      tier.el.classList.toggle('is-active', totalQty >= tier.min);
    }
  }

  #refreshAtc() {
    const totals = this.#getTotals();
    const atcBtn = this.querySelector('[data-bb-atc]');
    const atcText = this.querySelector('[data-bb-atc-text]');
    const counterEl = this.querySelector('[data-bb-counter]');

    this.#refreshTiers(totals.qty);
    this.#refreshRowPrices(totals.tier);

    if (counterEl) counterEl.textContent = String(totals.qty);

    if (!(atcBtn instanceof HTMLButtonElement) || !(atcText instanceof HTMLElement)) return;

    if (totals.qty < this.#minQty) {
      atcBtn.disabled = true;
      atcText.textContent = this.#requiredText;
      this.#renderAtcPrices(null);
      return;
    }

    atcBtn.disabled = false;
    atcText.textContent = this.#defaultText;
    this.#renderAtcPrices(totals);
  }

  /** @param {Element} row */
  #getRowCartVariantIds(row) {
    const ids = [row.dataset.variantId].filter(Boolean);
    if (row.dataset.mappedVariantIds) {
      row.dataset.mappedVariantIds.split(',').forEach((id) => {
        if (id) ids.push(id);
      });
    }
    return ids;
  }

  /** @param {{ items?: Array<{ variant_id: number, quantity: number }> }} cart */
  #syncRowsFromCart(cart) {
    if (!cart?.items) return;

    for (const row of this.#rows) {
      const ids = this.#getRowCartVariantIds(row);
      let qty = 0;

      for (const item of cart.items) {
        if (item.selling_plan_allocation) continue;
        if (ids.includes(String(item.variant_id))) qty += item.quantity;
      }

      this.#setRowQty(row, qty);
    }
  }

  /** @param {import('@shopify/events').CartLinesUpdateEvent} event */
  #handleCartLinesUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async () => {
        const cart = await this.#fetchCartJson();
        this.#syncRowsFromCart(cart);
      })
      .catch(() => {});
  };

  #collectItems() {
    return this.#rows
      .map((row) => ({
        id: Number(row.dataset.variantId),
        quantity: Number(row.querySelector('.mm-bundle-builder__qty-input')?.value) || 0,
      }))
      .filter((item) => item.id && item.quantity > 0);
  }

  #cartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter(Boolean)
      .join(',');
  }

  async #fetchCartJson() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    return response.json();
  }

  async #refreshCart() {
    const cartItems = document.querySelector('cart-items-component');
    if (cartItems) {
      await customElements.whenDefined('cart-items-component');
      return cartItems.fetchCartData();
    }
    return this.#fetchCartJson();
  }

  async #addBundleToCart() {
    if (this.#submitting) return;

    const items = this.#collectItems();
    if (items.length === 0) return;

    const atcBtn = this.querySelector('[data-bb-atc]');
    const atcText = this.querySelector('[data-bb-atc-text]');
    if (!(atcBtn instanceof HTMLButtonElement) || !(atcText instanceof HTMLElement)) return;

    this.#submitting = true;
    atcBtn.disabled = true;
    const previousText = atcText.textContent;
    atcText.textContent = 'Adding...';
    this.#renderAtcPrices(null);

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const sections = this.#cartSectionIds();
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items,
          ...(sections ? { sections } : {}),
          sections_url: window.location.pathname,
        }),
      }).then((res) => res.json());

      if (response.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );

        const cart = await this.#refreshCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'mm-bundle-builder', itemCount: totalQuantity },
        });
        return;
      }

      const cart = await this.#refreshCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'mm-bundle-builder',
          itemCount: totalQuantity,
          sections: response.sections,
        },
      });
    } catch (error) {
      console.error('Bundle ATC error:', error);
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      this.#submitting = false;
      this.#refreshAtc();
      if (atcText.textContent === 'Adding...') {
        atcText.textContent = previousText || this.#defaultText;
      }
    }
  }
}

if (!customElements.get('mm-bundle-builder')) {
  customElements.define('mm-bundle-builder', MmBundleBuilder);
}
